import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { Background, Controls, ReactFlow } from '@xyflow/react';
import { AppWindow, Check, CopyPlus, Eye, LoaderCircle, Maximize2, Replace, Rows2, Unplug, X } from 'lucide-react';
import type { UserFlowConnectionState } from '@/connection/UserFlowConnectionManager';
import type { SharedFlowSnapshot } from './realtimeFlow';
import { getNodeTypes } from './nodeRegistry';
import { FlowDisplayLanguageProvider, type FlowDisplayLanguage } from './displayLanguage';
import { isFixedNodeId } from './config/fixedNodes';
import type { SharedFlowCopyMode } from './duplicateSharedFlow';

const nodeTypes = getNodeTypes();
export type RemoteFlowViewMode = 'fullscreen' | 'split' | 'dialog';
export const DEFAULT_REMOTE_FLOW_VIEW_MODE: RemoteFlowViewMode = 'split';

const STATE_LABELS: Record<UserFlowConnectionState, string> = {
  idle: '未接続',
  connecting: '接続しています',
  connected: '閲覧中',
  disconnected: '接続が切断されました',
  failed: '接続できませんでした',
};

type RemoteFlowViewerProps = {
  displayLanguage?: FlowDisplayLanguage;
  displayName: string;
  state: UserFlowConnectionState;
  snapshot: SharedFlowSnapshot | null;
  error: string | null;
  mode: RemoteFlowViewMode;
  splitRatio: number;
  onSplitRatioChange: (ratio: number) => void;
  onModeChange: (mode: RemoteFlowViewMode) => void;
  onDuplicate: (mode: SharedFlowCopyMode) => void;
  onClose: () => void;
};

type DialogPosition = { left: number; top: number };
type DialogDrag = DialogPosition & { pointerId: number; width: number; height: number };
type SplitDrag = { pointerId: number; top: number; height: number };

const constrainSplitRatio = (ratio: number): number => Math.min(80, Math.max(20, ratio));

const VIEW_MODES: ReadonlyArray<{
  mode: RemoteFlowViewMode;
  label: string;
  icon: typeof Maximize2;
}> = [
  { mode: 'split', label: '上下に分割して比較', icon: Rows2 },
  { mode: 'dialog', label: '移動できるDialogで閲覧', icon: AppWindow },
  { mode: 'fullscreen', label: '全画面で閲覧', icon: Maximize2 },
];

export function RemoteFlowViewer({
  displayLanguage = 'ja',
  displayName,
  state,
  snapshot,
  error,
  mode,
  splitRatio,
  onSplitRatioChange,
  onModeChange,
  onDuplicate,
  onClose,
}: RemoteFlowViewerProps) {
  const connected = state === 'connected';
  const viewerRef = useRef<HTMLElement>(null);
  const duplicateControlRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DialogDrag | null>(null);
  const splitDragRef = useRef<SplitDrag | null>(null);
  const [dialogPosition, setDialogPosition] = useState<DialogPosition | null>(null);
  const [dragging, setDragging] = useState(false);
  const [splitDragging, setSplitDragging] = useState(false);
  const [duplicateComplete, setDuplicateComplete] = useState(false);
  const [duplicateMenuOpen, setDuplicateMenuOpen] = useState(false);
  const [replaceConfirmOpen, setReplaceConfirmOpen] = useState(false);
  const canDuplicate = snapshot?.nodes.some((node) => !isFixedNodeId(node.id)) ?? false;

  useEffect(() => {
    if (!duplicateComplete) return;
    const timeoutId = window.setTimeout(() => setDuplicateComplete(false), 2_000);
    return () => window.clearTimeout(timeoutId);
  }, [duplicateComplete]);

  useEffect(() => {
    if (!duplicateMenuOpen) return;
    const closeOutside = (event: PointerEvent) => {
      if (!duplicateControlRef.current?.contains(event.target as Node)) setDuplicateMenuOpen(false);
    };
    const closeWithEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDuplicateMenuOpen(false);
    };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeWithEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeWithEscape);
    };
  }, [duplicateMenuOpen]);

  const duplicate = (copyMode: SharedFlowCopyMode) => {
    if (copyMode === 'replace') {
      setDuplicateMenuOpen(false);
      setReplaceConfirmOpen(true);
      return;
    }
    onDuplicate(copyMode);
    setDuplicateMenuOpen(false);
    setDuplicateComplete(true);
  };

  const startSplitDragging = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (mode !== 'split') return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const workspace = viewerRef.current?.parentElement;
    if (!workspace) return;
    const rect = workspace.getBoundingClientRect();
    if (rect.height <= 0) return;
    splitDragRef.current = { pointerId: event.pointerId, top: rect.top, height: rect.height };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setSplitDragging(true);
    event.preventDefault();
  };

  const resizeSplit = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = splitDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const ratio = ((event.clientY - drag.top) / drag.height) * 100;
    onSplitRatioChange(Math.round(constrainSplitRatio(ratio) * 10) / 10);
    event.preventDefault();
  };

  const stopSplitDragging = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (splitDragRef.current?.pointerId !== event.pointerId) return;
    splitDragRef.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setSplitDragging(false);
  };

  const startDragging = (event: ReactPointerEvent<HTMLElement>) => {
    if (mode !== 'dialog') return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.target instanceof Element && event.target.closest('button')) return;
    const viewer = viewerRef.current;
    if (!viewer) return;
    const rect = viewer.getBoundingClientRect();
    dragRef.current = {
      pointerId: event.pointerId,
      left: event.clientX - rect.left,
      top: event.clientY - rect.top,
      width: rect.width,
      height: rect.height,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDragging(true);
    event.preventDefault();
  };

  const moveDialog = (event: ReactPointerEvent<HTMLElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    setDialogPosition({
      left: Math.min(Math.max(8, event.clientX - drag.left), Math.max(8, window.innerWidth - drag.width - 8)),
      top: Math.min(Math.max(8, event.clientY - drag.top), Math.max(8, window.innerHeight - drag.height - 8)),
    });
    event.preventDefault();
  };

  const stopDragging = (event: ReactPointerEvent<HTMLElement>) => {
    if (dragRef.current?.pointerId !== event.pointerId) return;
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setDragging(false);
  };

  return (
    <section
      ref={viewerRef}
      className={`remote-flow-viewer is-${mode}${dragging ? ' is-dragging' : ''}${splitDragging ? ' is-resizing' : ''}`}
      style={mode === 'dialog' && dialogPosition
        ? { left: dialogPosition.left, top: dialogPosition.top, transform: 'none' }
        : undefined}
      aria-label={`${displayName}のフローチャート`}
    >
      {mode === 'split' && (
        <div
          className="remote-flow-viewer__split-resizer"
          role="separator"
          aria-label="上下の表示比率を変更"
          aria-orientation="horizontal"
          aria-valuemin={20}
          aria-valuemax={80}
          aria-valuenow={Math.round(splitRatio)}
          tabIndex={0}
          onPointerDown={startSplitDragging}
          onPointerMove={resizeSplit}
          onPointerUp={stopSplitDragging}
          onPointerCancel={stopSplitDragging}
          onKeyDown={(event) => {
            if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
            onSplitRatioChange(constrainSplitRatio(splitRatio + (event.key === 'ArrowDown' ? 5 : -5)));
            event.preventDefault();
          }}
        >
          <span aria-hidden="true" />
        </div>
      )}
      <header
        className="remote-flow-viewer__header"
        onPointerDown={startDragging}
        onPointerMove={moveDialog}
        onPointerUp={stopDragging}
        onPointerCancel={stopDragging}
      >
        <div className="remote-flow-viewer__identity">
          <Eye size={18} aria-hidden="true" />
          <div>
            <span className="remote-flow-viewer__kind">閲覧フロー</span>
            <strong>{displayName}</strong>
            <span className={`remote-flow-viewer__status is-${state}`}>
              {state === 'connecting' && <LoaderCircle className="animate-spin" size={13} />}
              {(state === 'disconnected' || state === 'failed') && <Unplug size={13} />}
              {STATE_LABELS[state]}
            </span>
          </div>
        </div>
        <div className="remote-flow-viewer__actions">
          <div className="remote-flow-viewer__duplicate-control" ref={duplicateControlRef}>
            <button
              type="button"
              onClick={() => setDuplicateMenuOpen((open) => !open)}
              disabled={!canDuplicate}
              aria-label={duplicateComplete ? '複製しました' : '複製方法を選択'}
              aria-haspopup="menu"
              aria-expanded={duplicateMenuOpen}
              title={canDuplicate ? '自分のフローへ複製' : '複製できるカードがありません'}
              className={`remote-flow-viewer__duplicate${duplicateComplete ? ' is-complete' : ''}`}
            >
              {duplicateComplete
                ? <Check size={17} aria-hidden="true" />
                : <CopyPlus size={17} aria-hidden="true" />}
            </button>
            {duplicateMenuOpen && (
              <div className="remote-flow-viewer__duplicate-menu" role="menu" aria-label="複製方法">
                <button type="button" role="menuitem" onClick={() => duplicate('append')}>
                  <CopyPlus size={16} aria-hidden="true" />
                  <span><strong>現在のフローに追加</strong><small>今あるカードを残して右側に追加します</small></span>
                </button>
                <button type="button" role="menuitem" onClick={() => duplicate('replace')}>
                  <Replace size={16} aria-hidden="true" />
                  <span><strong>閲覧フローと同じにする</strong><small>現在のフローを置き換えます</small></span>
                </button>
              </div>
            )}
          </div>
          <div className="remote-flow-viewer__modes" role="group" aria-label="閲覧画面の表示方法">
            {VIEW_MODES.map(({ mode: nextMode, label, icon: Icon }) => (
              <button
                key={nextMode}
                type="button"
                onClick={() => onModeChange(nextMode)}
                aria-label={label}
                title={label}
                aria-pressed={mode === nextMode}
              >
                <Icon size={16} aria-hidden="true" />
              </button>
            ))}
          </div>
          <button type="button" onClick={onClose} aria-label="自分のフロー編集に戻る" title="自分のフロー編集に戻る">
            <X size={18} aria-hidden="true" />
          </button>
        </div>
      </header>
      {error && <p className="remote-flow-viewer__error" role="alert">{error}</p>}
      <div className="remote-flow-viewer__canvas">
        {snapshot ? (
          <FlowDisplayLanguageProvider value={displayLanguage}>
            <ReactFlow
              nodes={snapshot.nodes}
              edges={snapshot.edges}
              nodeTypes={nodeTypes}
              nodesDraggable={false}
              nodesConnectable={false}
              elementsSelectable={false}
              deleteKeyCode={null}
              panOnDrag
              zoomOnDoubleClick={false}
              fitView
              fitViewOptions={{ padding: 0.2 }}
              className="bg-background flow-strong-connectors"
            >
              <Background color="#d6b46c" />
              <Controls showInteractive={false} />
            </ReactFlow>
          </FlowDisplayLanguageProvider>
        ) : (
          <div className="remote-flow-viewer__empty">
            {connected ? 'フローチャートの受信を待っています' : '接続を準備しています'}
          </div>
        )}
      </div>
      {replaceConfirmOpen && (
        <div className="remote-flow-viewer__confirm-backdrop" role="presentation">
          <section
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="remote-flow-replace-title"
            aria-describedby="remote-flow-replace-description"
            className="remote-flow-viewer__confirm"
          >
            <h2 id="remote-flow-replace-title">自分のフローを置き換えますか？</h2>
            <p id="remote-flow-replace-description">
              現在のフローを削除し、{displayName}さんのフローと同じ内容にします。この操作はあとから元に戻せます。
            </p>
            <div>
              <button type="button" onClick={() => setReplaceConfirmOpen(false)}>キャンセル</button>
              <button
                type="button"
                className="is-danger"
                onClick={() => {
                  setReplaceConfirmOpen(false);
                  onDuplicate('replace');
                  setDuplicateComplete(true);
                }}
              >
                置き換える
              </button>
            </div>
          </section>
        </div>
      )}
    </section>
  );
}